import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  checkCanPurchasePass,
  fetchMultipleEntryPasses,
  selectCanPurchaseChecked,
  selectCanPurchaseLoading,
  selectPassesFetched,
} from "../../../store/multipleEntryPassSlice";
import { isPartnerClalit, isPartnerMaoflaamit, isPartnerOvdimBari } from "../../../store/partnerStore";
import { selectCarouselsFetchedFor } from "../../store/homeCarouselsSlice";
import useUserLocation from "../../hooks/useUserLocation";
import fetchAppParams from "../../utils/fetchAppParams";
import fetchHomeCarousels, { isSamePlace, locationKeyOf } from "../home/fetchHomeCarousels";

// Warms the store for the home page while the user reads the intro. Home reads
// all of this through selectors it already has, and its own effects short-circuit
// on the same "already loaded" guards used here — so this is purely an
// optimisation: on a hard refresh or a deep link, where welcome never runs, home
// still fetches everything itself.
//
// Every call is fire-and-forget and deliberately swalDialog-free: a prefetch must
// never throw a modal over the intro, and the CTA never waits on it.
export default function useWelcomePrefetch() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userSlice.mainUserData?.Token);
  const id = useSelector((state) => state.userSlice.mainUserData?.ID);
  const phone = useSelector((state) => state.userSlice.mainUserData?.Phone);
  const userData = useSelector((state) => state.userSlice.userData);
  const allAppParams = useSelector((state) => state.appParamSlice.allAppParams);
  const passesFetched = useSelector(selectPassesFetched);
  const canPurchaseChecked = useSelector(selectCanPurchaseChecked);
  const canPurchaseLoading = useSelector(selectCanPurchaseLoading);
  const isClalit = useSelector(isPartnerClalit);
  const isOvdimBari = useSelector(isPartnerOvdimBari);
  const isMaoflaamit = useSelector(isPartnerMaoflaamit);
  const fetchedForLocation = useSelector(selectCarouselsFetchedFor);
  const { coords } = useUserLocation();

  const binID = userData?.BinID;
  const appParamsLoaded = Object.keys(allAppParams).length > 0;
  // A stable stand-in for the object, so landing coordinates re-run the effect but
  // an unrelated render doesn't.
  const locationKey = locationKeyOf(coords);

  // The passes: credentials only, so this is the earliest it can possibly fire.
  useEffect(() => {
    if (passesFetched) return;
    if (!token || !id || !phone) return;
    dispatch(fetchMultipleEntryPasses({ token, id, phone }));
  }, [token, id, phone, passesFetched, dispatch]);

  // Purchase eligibility, same earliest-possible timing and the same partner
  // condition as mainPage.jsx — home's loader gate waits on this answer, so
  // warming it here means the gate is already open when home mounts.
  useEffect(() => {
    if (canPurchaseChecked || canPurchaseLoading) return;
    if (!(isClalit || isOvdimBari || isMaoflaamit)) return;
    if (!token || !id || !phone) return;
    dispatch(checkCanPurchasePass({ token, id, phone }));
  }, [token, id, phone, canPurchaseChecked, canPurchaseLoading, isClalit, isOvdimBari, isMaoflaamit, dispatch]);

  // The club list, for the card prices. Needs BinID, which arrives with the Login
  // response — later than the handshake, hence the dependency rather than a
  // mount-only effect.
  useEffect(() => {
    if (appParamsLoaded) return;
    if (!binID || !token || !id || !phone) return;
    fetchAppParams({ binID, token, id, phone, dispatch });
  }, [binID, token, id, phone, appParamsLoaded, dispatch]);

  // The carousels, but only when a location is already known — from the profile
  // address on the Login response, or one the user picked by hand. With no
  // location this would burn a round on "none" and then immediately
  // refetch once the CTA resolves GPS; those users get their first round on home
  // instead. Home's cache check covers the rest: it sees the locationKey claimed
  // here and issues nothing.
  useEffect(() => {
    if (!token || !id || !phone) return;
    if (!coords) return;
    // Same cache check home makes, and it doubles as the strict-mode guard: the
    // key is claimed synchronously, so a re-run can't start a second round.
    if (fetchedForLocation === locationKey) return;
    // The CTA's GPS fix landing a few dozen metres from the address this prefetch
    // already used must not re-fire the round — same guard home applies.
    if (isSamePlace(fetchedForLocation, coords)) return;
    fetchHomeCarousels({ coords, mode: "cold", dispatch, token, id, phone });
    // coords is read through locationKey, which is the stable form of it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id, phone, locationKey, fetchedForLocation, dispatch]);
}
